import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { hojeBR } from '../utils/data';
import pedirSenhaAdmin from '../components/common/pedirSenhaAdmin';
import { fecharComEsc, imprimirComprovanteMovimentacao } from '../utils/impressaoVenda';
import ModalConciliacaoCora from '../components/financeiro/ModalConciliacaoCora';
import s from './Financeiro.module.css';
import InputDataFiltro from '../components/common/InputDataFiltro';

const TIPOS_MOV = [
  { v: 'transferencia', l: 'Transferência entre contas' },
  { v: 'entrada',       l: 'Entrada avulsa'             },
  { v: 'saida',         l: 'Saída avulsa'               },
  { v: 'sangria',       l: 'Sangria de caixa'           },
  { v: 'suprimento',    l: 'Suprimento de caixa'        },
  { v: 'venda',         l: 'Venda'                      },
  { v: 'recebimento',   l: 'Recebimento'                },
];

function hoje()        { return hojeBR(); }
function inicioMes()   {
  const [ano, mes] = hoje().split('-');
  return `${ano}-${mes}-01`;
}
// Ancorado ao meio-dia local p/ subtrair dias sem cair no mesmo bug de fuso
// (hoje() já é Brasília-correto; T12:00 evita virar de dia ao formatar de volta).
function diasAtras(n) {
  const d = new Date(hoje() + 'T12:00:00');
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

export default function TransferenciaContas() {
  const [contas, setContas]   = useState([]);
  const [aba, setAba]         = useState('nova'); // nova | extrato

  // ── Nova movimentação ──────────────────────────────────────────
  const [form, setForm] = useState({
    tipo: 'transferencia', conta_origem_id: '', conta_destino_id: '',
    valor: '', descricao: '',
  });
  const [salvando, setSalvando] = useState(false);

  // ── Extrato ────────────────────────────────────────────────────
  const [contaSel, setContaSel]         = useState('');
  const [dtInicio, setDtInicio]         = useState(inicioMes());
  const [dtFim, setDtFim]               = useState(hoje());
  const [extrato, setExtrato]           = useState(null);
  const [carregando, setCarregando]     = useState(false);

  const [erro, setErro]       = useState('');
  const [sucesso, setSucesso] = useState('');

  // ── Alterar/Excluir lançamento manual (sem vínculo com outro módulo) ──
  const [modalAlterar, setModalAlterar] = useState(null); // { id, valor, descricao }
  const [salvandoAlt, setSalvandoAlt]   = useState(false);
  const [erroAlt, setErroAlt]           = useState('');

  // ── Conciliação Cora ────────────────────────────────────────────
  const [modalConciliacao, setModalConciliacao] = useState(false);

  const carregarContas = useCallback(async () => {
    try {
      const { data } = await api.get('/financeiro/contas');
      setContas(data.filter(c => c.ativo));
    } catch { setErro('Erro ao carregar contas.'); }
  }, []);

  useEffect(() => { carregarContas(); }, [carregarContas]);

  const carregarExtrato = useCallback(async () => {
    if (!contaSel) return;
    setCarregando(true); setErro('');
    try {
      const { data } = await api.get(
        `/financeiro/movimentacoes/extrato?conta_id=${contaSel}&data_inicio=${dtInicio}&data_fim=${dtFim}`
      );
      setExtrato(data);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao carregar extrato.');
      setExtrato(null);
    } finally { setCarregando(false); }
  }, [contaSel, dtInicio, dtFim]);

  useEffect(() => {
    if (aba === 'extrato' && contaSel) carregarExtrato();
  }, [aba, contaSel, carregarExtrato]);

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const precisaOrigem  = ['transferencia', 'saida',  'sangria' ].includes(form.tipo);
  const precisaDestino = ['transferencia', 'entrada', 'suprimento'].includes(form.tipo);

  const salvar = async () => {
    if (!form.valor || parseFloat(form.valor) <= 0) { setErro('Informe o valor.'); return; }
    if (precisaOrigem  && !form.conta_origem_id)    { setErro('Selecione a conta de origem.');  return; }
    if (precisaDestino && !form.conta_destino_id)   { setErro('Selecione a conta de destino.'); return; }
    if (form.tipo === 'transferencia' && form.conta_origem_id === form.conta_destino_id)
      { setErro('Conta de origem e destino devem ser diferentes.'); return; }

    setErro(''); setSalvando(true);
    try {
      await api.post('/financeiro/movimentacoes', {
        tipo:             form.tipo,
        conta_origem_id:  precisaOrigem  ? form.conta_origem_id  : null,
        conta_destino_id: precisaDestino ? form.conta_destino_id : null,
        valor:            parseFloat(form.valor),
        descricao:        form.descricao || null,
      });
      setSucesso('Movimentação registrada!');
      setTimeout(() => setSucesso(''), 2500);
      setForm({ tipo: 'transferencia', conta_origem_id: '', conta_destino_id: '', valor: '', descricao: '' });
      // Atualiza extrato se a conta afetada estiver selecionada
      if (contaSel && (form.conta_origem_id === contaSel || form.conta_destino_id === contaSel))
        carregarExtrato();
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao registrar.');
    } finally { setSalvando(false); }
  };

  const fmtBRL = v => {
    const n = parseFloat(v ?? 0);
    return n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmtDT = d => new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
  // 'devolucao' não entra em TIPOS_MOV (não pode ser criada manualmente), só é rotulada
  const tipoLabel = v => v === 'devolucao' ? 'Devolução' : (TIPOS_MOV.find(t => t.v === v)?.l || v);
  // Movimentos gerados por baixa de cartão/CR ganham rótulo específico
  const tipoMovLabel = m => {
    if (m.tipo === 'baixa_cr') return 'Baixa CR';
    if (m.baixa_id) {
      if (m.tipo === 'entrada') return 'Baixa cartão';
      if (m.tipo === 'saida')   return 'Taxas cartão';
    }
    return tipoLabel(m.tipo);
  };

  const contaNome = contas.find(c => String(c.id) === String(contaSel))?.nome || '';

  const imprimirExtrato = () => {
    if (!extrato) return;
    const { movimentos, saldo_anterior, total_entradas, total_saidas, total_periodo, saldo_final, periodo } = extrato;
    const linhas = [
      `EXTRATO — ${contaNome}`,
      `Período: ${new Date(periodo.inicio).toLocaleDateString('pt-BR')} a ${new Date(periodo.fim).toLocaleDateString('pt-BR')}`,
      '',
      `Saldo anterior: R$ ${fmtBRL(saldo_anterior)}`,
      `${'─'.repeat(80)}`,
      'Data/Hora            Tipo                 Descrição                  Valor         Saldo',
      `${'─'.repeat(80)}`,
      ...movimentos.map(m => {
        const sinal = m.variacao >= 0 ? '+' : '-';
        return [
          fmtDT(m.criado_em).padEnd(20),
          tipoMovLabel(m).padEnd(20),
          (m.descricao || '').substring(0, 24).padEnd(25),
          `${sinal}R$${fmtBRL(Math.abs(m.variacao))}`.padStart(14),
          `R$${fmtBRL(m.saldo_corrente)}`.padStart(14),
        ].join(' ');
      }),
      `${'─'.repeat(80)}`,
      `Entradas: R$ ${fmtBRL(total_entradas)}   Saídas: R$ ${fmtBRL(total_saidas)}   Total período: R$ ${fmtBRL(total_periodo)}`,
      `Saldo final: R$ ${fmtBRL(saldo_final)}`,
    ].join('\n');

    const html = `<!DOCTYPE html><html><head><meta charset="utf-8">
<style>body{font-family:Courier New,monospace;font-size:11px;padding:20px;white-space:pre;}</style>
</head><body>${linhas}</body></html>`;
    const win = window.open('', '_blank', 'width=800,height=600');
    if (!win) { alert('Permita pop-ups.'); return; }
    win.document.write(html);
    win.document.close();
    fecharComEsc(win);
    win.focus();
    win.print();
  };

  // Só lançamento manual (sem baixa/CR/CP/devolução/venda) pode ser alterado/excluído
  const semVinculo = m => !m.baixa_id && !m.conta_receber_id && !m.conta_pagar_id && !m.devolucao_id && !m.venda_id;

  const abrirAlterar = (m) => {
    setErroAlt('');
    setModalAlterar({ id: m.id, valor: String(parseFloat(m.valor)), descricao: m.descricao || '' });
  };

  const salvarAlteracao = async () => {
    if (!(parseFloat(modalAlterar.valor) > 0)) { setErroAlt('Informe um valor válido.'); return; }
    const senha = await pedirSenhaAdmin(
      'Alterar um lançamento financeiro exige senha de administrador ou gerente.',
      { acao: 'alterar_financeiro', detalhes: `Lançamento #${modalAlterar.id}` }
    );
    if (senha === null) return;
    setSalvandoAlt(true); setErroAlt('');
    try {
      await api.put(`/financeiro/movimentacoes/${modalAlterar.id}`, {
        valor: parseFloat(modalAlterar.valor),
        descricao: modalAlterar.descricao || null,
        senha_admin: senha,
      });
      setModalAlterar(null);
      await carregarExtrato();
    } catch (err) {
      setErroAlt(err.response?.data?.erro || 'Erro ao alterar.');
    } finally { setSalvandoAlt(false); }
  };

  const excluirLancamento = async (m) => {
    if (!window.confirm(`Excluir o lançamento de R$ ${fmtBRL(m.valor)} (${tipoMovLabel(m)})?`)) return;
    const senha = await pedirSenhaAdmin(
      'Excluir um lançamento financeiro exige senha de administrador ou gerente.',
      { acao: 'excluir_financeiro', detalhes: `Lançamento #${m.id} — R$ ${fmtBRL(m.valor)}` }
    );
    if (senha === null) return;
    try {
      await api.delete(`/financeiro/movimentacoes/${m.id}`, { data: { senha_admin: senha } });
      await carregarExtrato();
    } catch (err) {
      alert(err.response?.data?.erro || 'Erro ao excluir.');
    }
  };

  return (
    <div className={s.erpWrap}>
      <div className={s.titleBar}><span>🔄</span> Movimentações Financeiras</div>

      <div className={s.toolbar}>
        <button className={`${s.btnErp} ${aba === 'nova' ? s.btnErpPrimario : ''}`}
          onClick={() => setAba('nova')}>Nova Movimentação</button>
        <button className={`${s.btnErp} ${aba === 'extrato' ? s.btnErpPrimario : ''}`}
          onClick={() => setAba('extrato')}>Extrato por Conta</button>
        {aba === 'extrato' && extrato && (
          <button className={s.btnErp} onClick={imprimirExtrato} style={{ marginLeft: 'auto' }}>
            🖨 Imprimir
          </button>
        )}
      </div>

      {erro    && <div className={s.erroMsg}>{erro}</div>}
      {sucesso && <div className={s.sucessoMsg}>{sucesso}</div>}

      {/* ── NOVA MOVIMENTAÇÃO ── */}
      {aba === 'nova' && (
        <div className={s.conteudo} style={{ maxWidth: 560 }}>
          <div className={s.fieldset}>
            <div className={s.legend}>Nova Movimentação</div>

            <div className={s.formGrupo} style={{ marginBottom: 8 }}>
              <label className={s.label}>Tipo</label>
              <select className={`${s.select} ${s.inputLarge}`} value={form.tipo}
                onChange={e => set('tipo', e.target.value)}>
                {TIPOS_MOV.map(t => <option key={t.v} value={t.v}>{t.l}</option>)}
              </select>
            </div>

            {precisaOrigem && (
              <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                <label className={s.label}>Conta Origem *</label>
                <select className={`${s.select} ${s.inputLarge}`} value={form.conta_origem_id}
                  onChange={e => set('conta_origem_id', e.target.value)}>
                  <option value="">— selecione —</option>
                  {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}

            {precisaDestino && (
              <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                <label className={s.label}>Conta Destino *</label>
                <select className={`${s.select} ${s.inputLarge}`} value={form.conta_destino_id}
                  onChange={e => set('conta_destino_id', e.target.value)}>
                  <option value="">— selecione —</option>
                  {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </select>
              </div>
            )}

            <div className={s.formRow}>
              <div className={s.formGrupo}>
                <label className={s.label}>Valor (R$) *</label>
                <input className={s.input} type="number" step="0.01" min="0.01"
                  value={form.valor} onChange={e => set('valor', e.target.value)}
                  placeholder="0,00" style={{ width: 150 }} />
              </div>
              <div className={s.formGrupo} style={{ flex: 1 }}>
                <label className={s.label}>Descrição</label>
                <input className={`${s.input} ${s.inputLarge}`} value={form.descricao}
                  onChange={e => set('descricao', e.target.value)}
                  placeholder="Opcional..." />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <button className={s.btnErpPrimario} onClick={salvar} disabled={salvando}>
                {salvando ? 'Registrando...' : '✔ Registrar Movimentação'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EXTRATO ── */}
      {aba === 'extrato' && (
        <div className={s.conteudoSplit}>
          {/* Filtros laterais */}
          <div className={`${s.painel} ${s.painelLeft}`} style={{ width: 260 }}>
            <div style={{ padding: 12 }}>
              <div className={s.fieldset}>
                <div className={s.legend}>Filtros</div>

                <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                  <label className={s.label}>Conta *</label>
                  <select className={`${s.select} ${s.inputLarge}`}
                    value={contaSel} onChange={e => setContaSel(e.target.value)}>
                    <option value="">— selecione —</option>
                    {contas.map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                  </select>
                </div>

                <div className={s.formGrupo} style={{ marginBottom: 8 }}>
                  <label className={s.label}>Data Início</label>
                  <InputDataFiltro className={`${s.input} ${s.inputLarge}`}
                    value={dtInicio} onChange={e => setDtInicio(e.target.value)} />
                </div>

                <div className={s.formGrupo} style={{ marginBottom: 12 }}>
                  <label className={s.label}>Data Fim</label>
                  <InputDataFiltro className={`${s.input} ${s.inputLarge}`}
                    value={dtFim} onChange={e => setDtFim(e.target.value)} />
                </div>

                <button className={s.btnErpPrimario} onClick={carregarExtrato}
                  disabled={!contaSel || carregando} style={{ width: '100%' }}>
                  {carregando ? 'Carregando...' : '🔍 Consultar'}
                </button>

                {contas.find(c => String(c.id) === String(contaSel))?.integracao_boleto === 'cora' && (
                  <button className={s.btnErp} style={{ width: '100%', marginTop: 8 }}
                    onClick={() => setModalConciliacao(true)}>
                    🔄 Conciliar com Cora
                  </button>
                )}

                {/* Atalhos de período */}
                <div style={{ marginTop: 8, display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                  {[
                    { l: 'Hoje',    fn: () => { setDtInicio(hoje()); setDtFim(hoje()); } },
                    { l: 'Mês',     fn: () => { setDtInicio(inicioMes()); setDtFim(hoje()); } },
                    { l: 'Últ. 7d', fn: () => { setDtInicio(diasAtras(6)); setDtFim(hoje()); } },
                    { l: 'Últ. 30d', fn: () => { setDtInicio(diasAtras(29)); setDtFim(hoje()); } },
                  ].map(({ l, fn }) => (
                    <button key={l} className={s.btnErp}
                      style={{ flex: 1, fontSize: 11, height: 22, padding: '0 4px' }}
                      onClick={fn}>{l}</button>
                  ))}
                </div>
              </div>

              {/* Resumo do extrato */}
              {extrato && (
                <div className={s.fieldset}>
                  <div className={s.legend}>Resumo</div>
                  <div style={{ fontSize: 12, lineHeight: 2 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#555' }}>Saldo anterior</span>
                      <strong style={{ color: extrato.saldo_anterior >= 0 ? '#065f46' : '#b91c1c' }}>
                        R$ {fmtBRL(extrato.saldo_anterior)}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span style={{ color: '#065f46' }}>+ Entradas</span>
                      <span style={{ color: '#065f46' }}>R$ {fmtBRL(extrato.total_entradas)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#b91c1c' }}>− Saídas</span>
                      <span style={{ color: '#b91c1c' }}>R$ {fmtBRL(extrato.total_saidas)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#555' }}>Período</span>
                      <strong style={{ color: extrato.total_periodo >= 0 ? '#065f46' : '#b91c1c' }}>
                        {extrato.total_periodo >= 0 ? '+' : ''}R$ {fmtBRL(extrato.total_periodo)}
                      </strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
                      <strong>Saldo final</strong>
                      <strong style={{
                        fontSize: 14,
                        color: extrato.saldo_final >= 0 ? '#065f46' : '#b91c1c'
                      }}>
                        R$ {fmtBRL(extrato.saldo_final)}
                      </strong>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Tabela de extrato */}
          <div className={s.painelRight} style={{ padding: 0, overflow: 'auto' }}>
            {!contaSel && (
              <div style={{ padding: 24, color: '#888', fontSize: 12, textAlign: 'center' }}>
                Selecione uma conta e clique em Consultar.
              </div>
            )}
            {contaSel && carregando && (
              <div style={{ padding: 24, color: '#888', fontSize: 12 }}>Carregando...</div>
            )}
            {extrato && !carregando && (
              <>
                {/* Linha de saldo anterior */}
                <table className={s.tabela} style={{ tableLayout: 'fixed' }}>
                  <colgroup>
                    <col style={{ width: 135 }} />
                    <col style={{ width: 130 }} />
                    <col />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 110 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 70 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Data/Hora</th>
                      <th>Tipo</th>
                      <th>Descrição / Contrapartida</th>
                      <th style={{ textAlign: 'right' }}>Entrada</th>
                      <th style={{ textAlign: 'right' }}>Saída</th>
                      <th style={{ textAlign: 'right' }}>Saldo</th>
                      <th style={{ textAlign: 'center' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Saldo anterior como primeira linha */}
                    <tr style={{ background: '#f0f5fb', fontStyle: 'italic' }}>
                      <td colSpan={5} style={{ color: '#555', padding: '4px 8px', fontSize: 12 }}>
                        Saldo anterior ao período
                      </td>
                      <td style={{ textAlign: 'right', fontWeight: 'bold', padding: '4px 8px',
                        color: extrato.saldo_anterior >= 0 ? '#065f46' : '#b91c1c' }}>
                        R$ {fmtBRL(extrato.saldo_anterior)}
                      </td>
                      <td></td>
                    </tr>

                    {extrato.movimentos.length === 0 && (
                      <tr>
                        <td colSpan={7} style={{ textAlign: 'center', color: '#888',
                          padding: 16, fontSize: 12 }}>
                          Nenhuma movimentação no período.
                        </td>
                      </tr>
                    )}

                    {extrato.movimentos.map((m) => {
                      const entrada = m.variacao > 0 ? m.variacao : null;
                      const saida   = m.variacao < 0 ? Math.abs(m.variacao) : null;

                      // Descrição composta
                      let contrapartida = m.descricao || '';
                      if (m.tipo === 'transferencia') {
                        if (entrada) contrapartida = `Transf. de: ${m.conta_origem_nome || '—'}${m.descricao ? ` — ${m.descricao}` : ''}`;
                        if (saida)   contrapartida = `Transf. p/: ${m.conta_destino_nome || '—'}${m.descricao ? ` — ${m.descricao}` : ''}`;
                      } else {
                        const outra = m.conta_origem_nome || m.conta_destino_nome;
                        if (outra && !contrapartida) contrapartida = outra;
                        if (m.descricao && outra && outra !== m.descricao)
                          contrapartida = outra + (m.descricao ? ` — ${m.descricao}` : '');
                      }
                      if (!contrapartida) contrapartida = tipoLabel(m.tipo);

                      return (
                        <tr key={m.id}>
                          <td style={{ fontSize: 11 }}>{fmtDT(m.criado_em)}</td>
                          <td style={{ fontSize: 11 }}>{tipoMovLabel(m)}</td>
                          <td style={{ fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap' }} title={contrapartida}>
                            {contrapartida}
                          </td>
                          <td style={{ textAlign: 'right', color: '#065f46', fontWeight: entrada ? 'bold' : 'normal' }}>
                            {entrada != null ? `R$ ${fmtBRL(entrada)}` : ''}
                          </td>
                          <td style={{ textAlign: 'right', color: '#b91c1c', fontWeight: saida ? 'bold' : 'normal' }}>
                            {saida != null ? `R$ ${fmtBRL(saida)}` : ''}
                          </td>
                          <td style={{ textAlign: 'right', fontWeight: 'bold',
                            color: m.saldo_corrente >= 0 ? '#065f46' : '#b91c1c' }}>
                            R$ {fmtBRL(m.saldo_corrente)}
                          </td>
                          <td style={{ textAlign: 'center' }}>
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'center' }}>
                              {m.tipo === 'sangria' && (
                                <button className={s.btnErp} style={{ padding: '1px 6px', fontSize: 11 }}
                                  title="Imprimir comprovante da sangria"
                                  onClick={() => imprimirComprovanteMovimentacao(m)}>🖨️</button>
                              )}
                              {semVinculo(m) ? (
                                <>
                                  <button className={s.btnErp} style={{ padding: '1px 6px', fontSize: 11 }}
                                    title="Alterar valor/descrição (exige senha admin/gerente)"
                                    onClick={() => abrirAlterar(m)}>✏️</button>
                                  <button className={`${s.btnErp} ${s.btnErpPerigo}`} style={{ padding: '1px 6px', fontSize: 11 }}
                                    title="Excluir (exige senha admin/gerente)"
                                    onClick={() => excluirLancamento(m)}>✕</button>
                                </>
                              ) : (
                                m.tipo !== 'sangria' && (
                                  <span style={{ fontSize: 10, color: '#999' }} title="Gerado por outro módulo — desfaça por lá">—</span>
                                )
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  {/* Rodapé — resumo */}
                  <tfoot>
                    <tr style={{ background: '#dde6f0', fontWeight: 'bold', fontSize: 12 }}>
                      <td colSpan={3} style={{ padding: '5px 8px' }}>
                        Total do período ({extrato.movimentos.length} movimentação(ões))
                      </td>
                      <td style={{ textAlign: 'right', padding: '5px 8px', color: '#065f46' }}>
                        R$ {fmtBRL(extrato.total_entradas)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '5px 8px', color: '#b91c1c' }}>
                        R$ {fmtBRL(extrato.total_saidas)}
                      </td>
                      <td style={{ textAlign: 'right', padding: '5px 8px',
                        color: extrato.saldo_final >= 0 ? '#065f46' : '#b91c1c' }}>
                        R$ {fmtBRL(extrato.saldo_final)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal — Alterar lançamento manual */}
      {modalAlterar && (
        <div className={s.overlay}>
          <div className={s.modal} style={{ width: 380 }}>
            <div className={s.modalTitle}>Alterar Lançamento #{modalAlterar.id}</div>
            <div className={s.modalBody}>
              {erroAlt && (
                <div style={{ background: '#fef2f2', border: '1px solid #ef4444', borderRadius: 4,
                  padding: '6px 10px', fontSize: 12, color: '#b91c1c', marginBottom: 10 }}>{erroAlt}</div>
              )}
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12, marginBottom: 10 }}>
                Valor R$
                <input className={s.input} type="number" min="0.01" step="0.01"
                  value={modalAlterar.valor}
                  onChange={e => setModalAlterar(v => ({ ...v, valor: e.target.value }))} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 }}>
                Descrição
                <input className={s.input} value={modalAlterar.descricao}
                  onChange={e => setModalAlterar(v => ({ ...v, descricao: e.target.value }))} />
              </label>
            </div>
            <div className={s.modalFooter}>
              <button className={`${s.btnErp} ${s.btnErpPrimario}`} disabled={salvandoAlt} onClick={salvarAlteracao}>
                {salvandoAlt ? 'Salvando…' : 'Salvar'}
              </button>
              <button className={s.btnErp} disabled={salvandoAlt} onClick={() => setModalAlterar(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {modalConciliacao && (
        <ModalConciliacaoCora
          contaId={contaSel}
          contaNome={contas.find(c => String(c.id) === String(contaSel))?.nome || ''}
          dataInicio={dtInicio}
          dataFim={dtFim}
          onClose={() => setModalConciliacao(false)}
          onConciliado={carregarExtrato}
        />
      )}
    </div>
  );
}
